/**
 * FO-313 (batch-34) — Federatieve bibliotheek-index.
 *
 * De bibliotheek is een LEESLAAG: elk documenttype toont/doorzoekt de documenten
 * uit zijn bron-module (Invoice, Werkbon, Loonstrook, ...) via een bron-adapter,
 * met de rechten van die module en het bestand via de BESTAANDE beveiligde route.
 * Geen kopieën, geen tweede bestand-endpoint. Deze module bevat alleen de
 * gedeelde DTO's + query-schemas; de adapters leven in de API
 * (`apps/api/src/services/bibliotheek`).
 *
 * De 20 documenttypes komen uit `DOCUMENT_TYPE` (documenten.ts); een type zonder
 * werkende adapter toont "nog niet gekoppeld" i.p.v. een misleidende 0.
 */

import { z } from 'zod'
import { DOCUMENT_TYPE, type DocumentType } from './documenten'

/** Herkomst van een rij: uit de bron-module (federatief) of een geregistreerde upload. */
export const BIBLIOTHEEK_BRON = ['BRON', 'UPLOAD'] as const
export type BibliotheekBron = (typeof BIBLIOTHEEK_BRON)[number]

export const BIBLIOTHEEK_BRON_LABELS: Record<BibliotheekBron, string> = {
  BRON:   'Bron',
  UPLOAD: 'Upload',
}

/**
 * Eén teller-tegel in de Per type-weergave (FO-313 sectie 2.1).
 * - `aantal`/`nieuwDezeWeek` zijn `null` wanneer het type nog geen adapter heeft
 *   (`gekoppeld === false`) -> de UI toont "nog niet gekoppeld", NOOIT een 0.
 * - Alleen types die de gebruiker mag zien worden teruggegeven (rechten
 *   server-side; default: verbergen). Zie sectie 2.2 / acceptatiecriterium 3.
 */
export interface BibliotheekTellerDTO {
  type: DocumentType
  label: string
  /** Zichtbaar aantal documenten uit de bron; `null` = nog niet gekoppeld. */
  aantal: number | null
  /** Documenten uit de afgelopen 7 dagen; `null` = nog niet gekoppeld. */
  nieuwDezeWeek: number | null
  /** Adapter aangesloten? `false` -> "nog niet gekoppeld". */
  gekoppeld: boolean
}

/**
 * Eén rij in een type-lijst of zoekresultaat. Bron-agnostisch: elke adapter
 * mapt zijn domeinmodel hierop. Alles behalve `id`/`type`/`titel`/`bron`/
 * `openInModulePad` mag `null` zijn zodat elk type netjes past.
 */
export interface BibliotheekRowDTO {
  /** Samengestelde, over bronnen unieke id: `<type>:<bronId>`. */
  id: string
  type: DocumentType
  typeLabel: string
  /** Bronmodel-id (doorklik + download-resolutie). */
  bronId: string
  /** Kenmerk/nummer (factuurnummer, werkbonnummer, ...) of `null`. */
  nummer: string | null
  /** Titel/omschrijving (nooit leeg; adapter vult een zinnige fallback). */
  titel: string
  /** Primaire datum als ISO-8601 (UTC); UI formatteert in Europe/Amsterdam. */
  datum: string | null
  /** Status-badge uit de bron ("Definitief", "Concept", "Vergrendeld", ...). */
  status: string | null
  /** Gekoppelde relatie (klant). */
  relatieId: string | null
  relatieNaam: string | null
  /** Gekoppelde medewerker. */
  medewerkerId: string | null
  medewerkerNaam: string | null
  /** Herkomst-badge. */
  bron: BibliotheekBron
  /** Heeft het document een bestand? `false` -> "nog geen PDF" (+ evt. render-actie). */
  heeftBestand: boolean
  /**
   * Mime van het bestand als viewer-/download-hint. `null` = onbekend; de viewer
   * leidt het type dan af uit de Content-Type van de bron-route. Voorkomt dat
   * een niet-PDF-bijlage (bonfoto, scan) als PDF wordt behandeld.
   */
  mimeType: string | null
  /**
   * Same-origin backoffice-proxy-pad voor preview/download (`/api/bibliotheek/
   * bestand/<type>/<bronId>`), of `null` als er (nog) geen bestand is. De proxy
   * bridget cookie -> Bearer en forwardt naar de BESTAANDE beveiligde route van
   * de bron; de bibliotheek bouwt GEEN tweede bestand-endpoint dat zelf bytes
   * serveert.
   */
  downloadPad: string | null
  /** Backoffice-deeplink "open in module" (de bron blijft de werkplek). */
  openInModulePad: string
}

/**
 * Eén type-groep in het federatieve zoekresultaat. Een falende adapter zet
 * `fout: true` op zijn eigen groep; de rest blijft bruikbaar (allSettled).
 */
export interface BibliotheekZoekGroepDTO {
  type: DocumentType
  typeLabel: string
  /** Totaal aantal treffers in de bron (voor "toon alle {n}"). */
  totaal: number
  /** Top-N treffers (per-bron-limiet). */
  items: BibliotheekRowDTO[]
  /** Adapter faalde -> UI toont een foutmelding voor deze sectie. */
  fout: boolean
}

// ─── Query-schemas (gedeeld: API-validatie + client) ──────────────────────────

const YMD = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum in formaat jjjj-mm-dd')

/** `GET /bibliotheek/type/:type` — gepagineerde lijst met filters. */
export const bibliotheekTypeLijstQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  /** Vrije zoekterm binnen dit type (nummer/titel/relatie/medewerker). */
  search: z.string().trim().max(200).optional(),
  clientId: z.string().min(1).optional(),
  employeeId: z.string().min(1).optional(),
  datumVan: YMD.optional(),
  datumTot: YMD.optional(),
  /** Werkbon/factuur: toon ook concepten (die tellen niet mee op de tegel). */
  inclConcept: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
})
export type BibliotheekTypeLijstQuery = z.infer<typeof bibliotheekTypeLijstQuerySchema>

/**
 * `GET /bibliotheek/zoek` — federatief zoeken over alle zichtbare adapters.
 * Een zoekterm (>= 2 tekens) OF een relatie-/medewerker-filter is verplicht:
 * zonder term maar mét `clientId`/`employeeId` levert het "alle documenten van
 * deze klant/medewerker" (de contextlinks vanaf de detailpagina's).
 */
export const bibliotheekZoekQuerySchema = z
  .object({
    term: z.string().trim().max(200).optional(),
    /** Beperk tot één type (bv. bij "toon alle {n}"). */
    type: z.enum(DOCUMENT_TYPE).optional(),
    clientId: z.string().min(1).optional(),
    employeeId: z.string().min(1).optional(),
    datumVan: YMD.optional(),
    datumTot: YMD.optional(),
    /** Per-bron-limiet (top-N per type). "toon alle" pagineert via de type-lijst. */
    limietPerBron: z.coerce.number().int().min(1).max(25).default(5),
  })
  .refine((v) => (v.term !== undefined && v.term.length >= 2) || Boolean(v.clientId) || Boolean(v.employeeId), {
    message: 'Geef een zoekterm van minimaal 2 tekens of een relatie/medewerker-filter',
    path: ['term'],
  })
export type BibliotheekZoekQuery = z.infer<typeof bibliotheekZoekQuerySchema>

/** `:type`-padparam. */
export const bibliotheekTypeParamSchema = z.object({
  type: z.enum(DOCUMENT_TYPE),
})
